import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser, signUpWithEmail, normalisePhone } from '@/lib/auth';
import { createRestaurant } from '@/lib/data';

/* Onboarding wizard — marketing design language. Creates the restaurant in Supabase → /dashboard. */
const STEPS = ['Account', 'Restaurant', 'Outlet & Tables', 'Menu', 'Done'];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [verified, setVerified] = useState(false); // = account created / already signed in
  const [bizName, setBizName] = useState('');
  const [address, setAddress] = useState('');
  const [stateName, setStateName] = useState('');
  const [pincode, setPincode] = useState('');
  const [gstin, setGstin] = useState('');
  const [fssai, setFssai] = useState('');
  const [gstType, setGstType] = useState<5 | 18>(5);
  const [tableCount, setTableCount] = useState(8);
  const [menuChoice, setMenuChoice] = useState<'import' | 'scratch' | null>(null);
  const [importSource, setImportSource] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Already signed in (e.g. just logged in but has no restaurant yet)?
  // Mark the account step as done and prefill what we know.
  useEffect(() => {
    let cancelled = false;
    getCurrentUser().then((u) => {
      if (cancelled || !u) return;
      setVerified(true);
      if (u.phoneNumber) setPhone(u.phoneNumber);
      if (u.email) setOwnerEmail(u.email);
      if (u.displayName) setOwnerName(u.displayName);
    });
    return () => { cancelled = true; };
  }, []);

  const back = () => setStep((s) => Math.max(s - 1, 0));
  const canNext = () => {
    if (step === 0) return verified;
    if (step === 1) return !!bizName.trim();
    if (step === 3) return menuChoice !== null;
    return true;
  };

  const createAccount = async () => {
    const email = ownerEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErr('Enter a valid email address.'); return; }
    if (password.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    if (password !== confirmPwd) { setErr('Passwords do not match.'); return; }
    if (phone.trim() && normalisePhone(phone).replace(/\D/g, '').length < 11) {
      setErr('Enter a valid mobile number with country code, or leave it empty.'); return;
    }
    try {
      setBusy(true); setErr('');
      await signUpWithEmail(email, password, ownerName.trim() || undefined);
      setVerified(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create your account.');
    } finally { setBusy(false); }
  };

  const next = async () => {
    if (step === 3) {
      try {
        setBusy(true); setErr('');
        const fullAddress = [address.trim(), stateName.trim(), pincode.trim()].filter(Boolean).join(', ');
        await createRestaurant({ name: bizName, ownerName, ownerEmail, ownerPhone: phone.trim() ? normalisePhone(phone) : undefined, address: fullAddress, gstin, fssai, gstRate: gstType, tableCount });
        setStep(4);
      } catch (e) {
        // Supabase PostgrestError is a plain object, not an Error instance —
        // surface its message/details so the real cause is visible.
        const pe = e as { message?: string; details?: string; hint?: string } | null;
        const msg = [pe?.message, pe?.details, pe?.hint].filter(Boolean).join(' — ');
        setErr(msg || 'Could not create your restaurant.');
      } finally { setBusy(false); }
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  return (
    <div className="ezmkt wiz">
      <header className="wiz-top">
        <Link className="brand" to="/"><img src="/images/logo.png" alt="EzyDinePro" style={{ height: 34, width: "auto" }} /></Link>
        <span className="sp">Set up your restaurant</span>
      </header>

      {/* mobile dots */}
      <div className="wiz-dots">
        {STEPS.map((s, i) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: i < STEPS.length - 1 ? 1 : '0 0 auto' }}>
            <div className={`d${i <= step ? ' on' : ''}`}>{i < step ? <i className="ri-check-line" /> : i + 1}</div>
            {i < STEPS.length - 1 && <div className={`ln${i < step ? ' on' : ''}`} />}
          </div>
        ))}
      </div>

      <div className="wiz-body">
        <aside className="wiz-rail">
          <div className="cap">Setup</div>
          {STEPS.map((s, i) => {
            const state = i < step ? 'done' : i === step ? 'active' : 'todo';
            return (
              <div key={s} className={`step ${state}`}>
                <div className="num">{state === 'done' ? <i className="ri-check-line" /> : i + 1}</div>
                <div className="nm">{s}</div>
              </div>
            );
          })}
        </aside>

        <main className="wiz-main">
          <div className="wiz-content">
            <div className="wiz-inner">
              {err && (
                <div className="auth-err" style={{ marginBottom: 16 }}>
                  <i className="ri-error-warning-line" /> {err}
                </div>
              )}
              {step === 0 && (
                <Step title="Create your account" sub="One email & password for the dashboard, POS and Captain apps.">
                  <WField label="Your name" value={ownerName} onChange={setOwnerName} placeholder="Rajesh Hegde" />
                  <WField label="Email address" value={ownerEmail} onChange={setOwnerEmail} placeholder="you@example.com" type="email" />
                  <label className="wfield">
                    <span className="lbl">Mobile number</span>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                    />
                  </label>
                  {!verified && (
                    <>
                      <div className="wrow">
                        <WField label="Password" value={password} onChange={setPassword} placeholder="Min. 6 characters" type="password" />
                        <WField label="Confirm password" value={confirmPwd} onChange={setConfirmPwd} placeholder="Repeat password" type="password" />
                      </div>
                      <button className="btn btn-teal" onClick={createAccount} disabled={busy}>
                        {busy && <i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite' }} />}
                        Create account
                      </button>
                    </>
                  )}
                  {verified && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--st-free)', fontWeight: 700 }}>
                      <i className="ri-checkbox-circle-fill" /> Account ready
                    </div>
                  )}
                </Step>
              )}

              {step === 1 && (
                <Step title="Restaurant details" sub="Used on your bills and GST invoices.">
                  <WField label="Restaurant name" value={bizName} onChange={setBizName} placeholder="Spice Garden" />
                  <WField label="Address" value={address} onChange={setAddress} placeholder="Koramangala, Bengaluru" />
                  <div className="wrow">
                    <WField label="State" value={stateName} onChange={setStateName} placeholder="Karnataka" />
                    <WField label="PIN code" value={pincode} onChange={(v) => setPincode(v.replace(/\D/g, '').slice(0, 6))} placeholder="560095" />
                  </div>
                  <div className="wrow">
                    <WField label="GSTIN" value={gstin} onChange={setGstin} placeholder="29ABCDE1234F1Z5" />
                    <WField label="FSSAI" value={fssai} onChange={setFssai} placeholder="11223344000055" />
                  </div>
                  <span className="wfield"><span className="lbl">Restaurant type</span></span>
                  <div className="wrow">
                    <div className={`wcard${gstType === 5 ? ' on' : ''}`} onClick={() => setGstType(5)}>
                      <div className="t">Standalone · 5%</div><div className="s">No input tax credit</div>
                    </div>
                    <div className={`wcard${gstType === 18 ? ' on' : ''}`} onClick={() => setGstType(18)}>
                      <div className="t">Hotel · 18%</div><div className="s">Room tariff &gt; ₹7,500</div>
                    </div>
                  </div>
                </Step>
              )}

              {step === 2 && (
                <Step title="Outlet & tables" sub="We'll generate a QR code & link for each table automatically.">
                  <div className="stepper">
                    <button className="b" onClick={() => setTableCount((c) => Math.max(1, c - 1))}><i className="ri-subtract-line" /></button>
                    <div style={{ textAlign: 'center' }}>
                      <div className="v">{tableCount}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>tables</div>
                    </div>
                    <button className="b" onClick={() => setTableCount((c) => c + 1)}><i className="ri-add-line" /></button>
                  </div>
                  <div className="wcard" style={{ cursor: 'default', marginTop: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: 'var(--teal)' }}>
                      <i className="ri-qr-code-line" /> {tableCount} QR codes will be created
                    </div>
                    <div className="qrgrid">
                      {Array.from({ length: Math.min(tableCount, 12) }).map((_, i) => (
                        <div className="cell" key={i}><i className="ri-qr-code-line" style={{ fontSize: 18 }} /></div>
                      ))}
                      {tableCount > 12 && <div className="cell" style={{ fontSize: 11, fontWeight: 700 }}>+{tableCount - 12}</div>}
                    </div>
                    <p style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>Each links to <code style={{ color: 'var(--teal-2)' }}>/t/your-outlet/T#</code> — print them as table tents later.</p>
                  </div>
                </Step>
              )}

              {step === 3 && (
                <Step title="Add your menu" sub="Bring it across in minutes, or start fresh.">
                  <div className={`wcard${menuChoice === 'import' ? ' on' : ''}`} onClick={() => setMenuChoice('import')} style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--panel)', color: 'var(--teal)', display: 'grid', placeItems: 'center', fontSize: 20 }}><i className="ri-download-2-line" /></div>
                    <div><div className="t">Import existing menu</div><div className="s">From Petpooja, Vyapar or a spreadsheet</div></div>
                  </div>
                  {menuChoice === 'import' && (
                    <div className="wrow" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 12 }}>
                      {['Petpooja', 'Vyapar', 'CSV / Excel'].map((s) => (
                        <div key={s} className={`wcard${importSource === s ? ' on' : ''}`} onClick={() => setImportSource(s)} style={{ textAlign: 'center', fontWeight: 700, padding: 12 }}>{s}</div>
                      ))}
                    </div>
                  )}
                  <div className={`wcard${menuChoice === 'scratch' ? ' on' : ''}`} onClick={() => setMenuChoice('scratch')} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--panel)', color: 'var(--teal)', display: 'grid', placeItems: 'center', fontSize: 20 }}><i className="ri-add-circle-line" /></div>
                    <div><div className="t">Start from scratch</div><div className="s">Add categories &amp; items yourself</div></div>
                  </div>
                </Step>
              )}

              {step === 4 && (
                <div style={{ textAlign: 'center', paddingTop: 12 }}>
                  <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(43,165,121,.12)', display: 'grid', placeItems: 'center', margin: '0 auto' }}>
                    <i className="ri-check-line" style={{ fontSize: 40, color: 'var(--st-free)' }} />
                  </div>
                  <h1 style={{ marginTop: 18 }}>You're all set!</h1>
                  <p className="sub">{bizName || 'Your restaurant'} is ready. A few things to finish:</p>
                  <div style={{ maxWidth: 380, margin: '22px auto 0', display: 'grid', gap: 10, textAlign: 'left' }}>
                    {['Print & place your table QR codes', 'Add your staff & their roles', 'Connect a printer for KOT & bills'].map((t) => (
                      <div key={t} className="wcard" style={{ cursor: 'default', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
                        <i className="ri-checkbox-blank-circle-line" style={{ color: 'var(--line-strong)' }} /> <span style={{ fontSize: 14 }}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="wiz-foot">
            <div className="inner">
              {step > 0 && step < 4 && <button className="btn btn-ghost" onClick={back}>Back</button>}
              <div style={{ flex: 1 }} />
              {step < 4 ? (
                <button className="btn btn-teal" onClick={next} disabled={!canNext() || busy} style={{ opacity: !canNext() || busy ? 0.5 : 1 }}>
                  {busy && <i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite' }} />}
                  {step === 3 ? 'Finish setup' : 'Continue'} <i className="ri-arrow-right-line" />
                </button>
              ) : (
                <button className="btn btn-orange" onClick={() => navigate('/dashboard')}>Go to dashboard <i className="ri-arrow-right-line" /></button>
              )}
            </div>
          </div>
        </main>
      </div>
      {/* Firebase invisible reCAPTCHA mounts here */}
      <div id="recaptcha-container" />
    </div>
  );
}

function Step({ title, sub, children }: { title: string; sub: string; children: ReactNode }) {
  return (
    <div>
      <h1>{title}</h1>
      <p className="sub">{sub}</p>
      <div style={{ marginTop: 24 }}>{children}</div>
    </div>
  );
}

function WField({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="wfield">
      <span className="lbl">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}
